import axios from "axios";

// Use the current browser origin for API calls — this ensures the app works
// on any domain (custom domain, emergent.host, or preview) without env config
const BASE = `${window.location.origin}/api`;

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
});

// Intercept 401 to clear token & redirect
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && window.location.pathname.startsWith("/admin/")) {
      localStorage.removeItem("admin_token");
      window.location.href = "/admin";
    }
    return Promise.reject(err);
  }
);

export const api = {
  // Public
  getAvailableSlots: () => axios.get(`${BASE}/available-slots`),
  verifyAvailabilityPassword: (password) => axios.post(`${BASE}/availability/verify`, { password }),
  getPublicAvailability: (weeks, password) => axios.get(`${BASE}/public/availability?weeks=${weeks}`, { headers: { Authorization: `Bearer ${password}` } }),
  getEmbedConfig: () => axios.get(`${BASE}/embed-config`),
  getMaintenanceStatus: () => axios.get(`${BASE}/maintenance-status`),
  createBooking: (data) => axios.post(`${BASE}/bookings`, data),
  createBookingAsAdmin: (data) => axios.post(`${BASE}/bookings`, data, authHeaders()),
  validateBookingCode: (code) => axios.get(`${BASE}/booking-codes/validate/${encodeURIComponent(code)}`),
  getClaimDetails: (token) => axios.get(`${BASE}/bookings/claim/${token}`),
  claimBooking: (token, timeOverride = null, bike = null) => axios.post(`${BASE}/bookings/claim/${token}`, { time_override: timeOverride, bike }),

  // Admin auth
  login: (email, password) => axios.post(`${BASE}/auth/login`, { email, password }),
  getMe: () => axios.get(`${BASE}/auth/me`, authHeaders()),

  // Admin bookings
  getAdminBookings: () => axios.get(`${BASE}/admin/bookings`, authHeaders()),
  adminCreateBooking: (data) => axios.post(`${BASE}/admin/bookings`, data, authHeaders()),
  updateBooking: (id, data) => axios.put(`${BASE}/admin/bookings/${id}`, data, authHeaders()),
  deleteBooking: (id) => axios.delete(`${BASE}/admin/bookings/${id}`, authHeaders()),
  cancelBooking: (id, reason, sendNotifications = true) => axios.post(`${BASE}/admin/bookings/${id}/cancel`, { reason, send_notifications: sendNotifications }, authHeaders()),
  resendVolunteerNotifications: (id) => axios.post(`${BASE}/admin/bookings/${id}/resend-volunteers`, {}, authHeaders()),

  // Archive
  archiveBookings: (bookingIds) => axios.post(`${BASE}/admin/bookings/archive`, { booking_ids: bookingIds }, authHeaders()),
  unarchiveBookings: (bookingIds) => axios.post(`${BASE}/admin/bookings/unarchive`, { booking_ids: bookingIds }, authHeaders()),
  getArchivedBookings: () => axios.get(`${BASE}/admin/bookings/archived`, authHeaders()),

  // Volunteers
  getVolunteers: () => axios.get(`${BASE}/volunteers`, authHeaders()),
  addVolunteer: (data) => axios.post(`${BASE}/volunteers`, data, authHeaders()),
  updateVolunteer: (id, data) => axios.put(`${BASE}/volunteers/${id}`, data, authHeaders()),
  deleteVolunteer: (id) => axios.delete(`${BASE}/volunteers/${id}`, authHeaders()),

  // Settings
  getSettings: () => axios.get(`${BASE}/settings`, authHeaders()),
  updateSettings: (data) => axios.put(`${BASE}/settings`, data, authHeaders()),
  sendTestEmail: (emailType, recipientEmail) => axios.post(`${BASE}/admin/email-admin/test`, { email_type: emailType, recipient_email: recipientEmail }, authHeaders()),
  testSmtpConnection: () => axios.post(`${BASE}/admin/smtp-test`, {}, authHeaders()),
  previewEmail: (emailType, intro, outro, fields) => axios.post(`${BASE}/admin/email-admin/preview`, { email_type: emailType, intro, outro, fields }, authHeaders()),
  getTestimonials: (includeHidden = false) => axios.get(`${BASE}/admin/testimonials${includeHidden ? "?include_hidden=true" : ""}`, authHeaders()),
  sendReviewRequest: (bookingId, subject, body) => axios.post(`${BASE}/admin/testimonials/${bookingId}/send-review`, { subject, body }, authHeaders()),
  hideTestimonial: (bookingId) => axios.delete(`${BASE}/admin/testimonials/${bookingId}`, authHeaders()),
  bulkHideTestimonials: (ids) => axios.post(`${BASE}/admin/testimonials/bulk-hide`, { ids }, authHeaders()),
  restoreTestimonial: (bookingId) => axios.post(`${BASE}/admin/testimonials/${bookingId}/restore`, {}, authHeaders()),
  // Super admin
  listAdmins: () => axios.get(`${BASE}/admin/super-admin/admins`, authHeaders()),
  createAdmin: (email, password) => axios.post(`${BASE}/admin/super-admin/admins`, { email, password }, authHeaders()),
  deleteAdmin: (aid) => axios.delete(`${BASE}/admin/super-admin/admins/${aid}`, authHeaders()),
  changeAdminPassword: (aid, newPassword) => axios.put(`${BASE}/admin/super-admin/admins/${aid}/password`, { new_password: newPassword }, authHeaders()),
  getAdminStats: () => axios.get(`${BASE}/admin/stats`, authHeaders()),
  getVolunteerStats: () => axios.get(`${BASE}/admin/volunteer-stats`, authHeaders()),
  // Booking Codes
  getBookingCodes: () => axios.get(`${BASE}/admin/booking-codes`, authHeaders()),
  createBookingCode: (code) => axios.post(`${BASE}/admin/booking-codes`, { code }, authHeaders()),
  deleteBookingCode: (id) => axios.delete(`${BASE}/admin/booking-codes/${id}`, authHeaders()),
  // Cancel individual bike slot
  cancelBike: (bookingId, bike) => axios.post(`${BASE}/admin/bookings/${bookingId}/cancel-bike`, { bike }, authHeaders()),
  
  // Suppressed Emails (SES Bounces/Complaints)
  getSuppressedEmails: () => axios.get(`${BASE}/admin/suppressed-emails`, authHeaders()),
  removeSuppressedEmail: (email) => axios.delete(`${BASE}/admin/suppressed-emails/${encodeURIComponent(email)}`, authHeaders()),

  // Email Logs
  getEmailLogs: (params = {}) => axios.get(`${BASE}/admin/email-logs`, { ...authHeaders(), params }),

  // Manual Volunteer Hours
  getManualHours: () => axios.get(`${BASE}/admin/manual-hours`, authHeaders()),
  addManualHours: (data) => axios.post(`${BASE}/admin/manual-hours`, data, authHeaders()),
  deleteManualHours: (id) => axios.delete(`${BASE}/admin/manual-hours/${id}`, authHeaders()),
  
  // Blocked Times
  getBlockedTimes: () => axios.get(`${BASE}/settings/blocked-times`, authHeaders()),
  addBlockedTime: (data) => axios.post(`${BASE}/settings/blocked-times`, data, authHeaders()),
  removeBlockedTime: (data) => axios.delete(`${BASE}/settings/blocked-times`, { ...authHeaders(), data }),
  
  // CSV Exports
  exportBookingsCSV: (status = "all") => axios.get(`${BASE}/admin/export/bookings?status=${status}`, { ...authHeaders(), responseType: "blob" }),
  exportVolunteersCSV: () => axios.get(`${BASE}/admin/export/volunteers`, { ...authHeaders(), responseType: "blob" }),
  exportVolunteerHoursCSV: () => axios.get(`${BASE}/admin/export/volunteer-hours`, { ...authHeaders(), responseType: "blob" }),
  
  // Volunteer Portal
  volunteerRequestLogin: (email) => axios.post(`${BASE}/volunteer/request-login`, { email }),
  volunteerVerifyLogin: (token) => axios.post(`${BASE}/volunteer/verify-login?token=${encodeURIComponent(token)}`),
  volunteerGetProfile: () => axios.get(`${BASE}/volunteer/me`, volunteerAuthHeaders()),
  volunteerGetAssignments: () => axios.get(`${BASE}/volunteer/assignments`, volunteerAuthHeaders()),
  volunteerGetAvailable: () => axios.get(`${BASE}/volunteer/available-bookings`, volunteerAuthHeaders()),
  volunteerClaimBooking: (bookingId, bikeType = null) => axios.post(`${BASE}/volunteer/claim/${bookingId}${bikeType ? `?bike_type=${encodeURIComponent(bikeType)}` : ''}`, {}, volunteerAuthHeaders()),

  // Requester Portal
  requesterRequestLogin: (email) => axios.post(`${BASE}/requester/request-login`, { email }),
  requesterVerifyLogin: (token) => axios.post(`${BASE}/requester/verify-login?token=${encodeURIComponent(token)}`),
  requesterGetBookings: () => {
    const token = localStorage.getItem("requester_token");
    return axios.get(`${BASE}/requester/bookings`, { headers: { Authorization: `Bearer ${token}` } });
  },
};

// Volunteer auth header helper
function volunteerAuthHeaders() {
  const token = localStorage.getItem("volunteer_token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

export default api;
